/** @jsx jsx */
import { Fragment } from 'react'
import { Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { Box, Heading, jsx, Text } from 'theme-ui'
import GithubSlugger from 'github-slugger'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import MediaEmbed from '~/components/Generic/MediaEmbed'

/**
 * A default placeholder template for unsupported nodes.
 */
const defaultInline = (_type, node) => {
  return (
    <span key={node.data.target.sys.id}>
      type: {node.nodeType} id: {node.data.target.sys.id}
    </span>
  )
}

/**
 * Used to generate IDs for article headings.
 */
const slugger = new GithubSlugger()

/**
 * Adds prefixes to linked slugs based on content type.
 */
const slug = (node) => {
  const type = node?.data?.target?.__typename
  const slug = node?.data?.target?.slug
  const slugUrl = node?.data?.target?.slugUrl
  const pathOverride = node?.data?.target?.path

  switch (type) {
    case 'ContentfulArticle':
      return `/blog/${slug}/`

    case 'ContentfulMerch':
    case 'ContentfulPack':
      return `/products/${slugUrl || slug}/`

    default:
      return slug === 'home' ? '/' : `/${pathOverride || slug}/`
  }
}

const hyperlinkProps = (uri, children) =>
  uri[0] !== '#' && uri[0] !== '/'
    ? {
        target: '_blank',
        rel: 'noreferrer',
        title: `${children.join(' ')} (opens in a new window)`,
      }
    : {}

// @see https://css-tricks.com/hash-tag-links-padding/
const headingAnchorOffsetStyles = {
  '&:before': {
    display: 'block',
    content: '""',
    marginTop: '-140px',
    height: '140px',
    visibility: 'hidden',
    pointerEvents: 'none',
  },
}

/**
 * Setting the rendering options. Same as:
 * https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer
 *
 * Also see https://github.com/gatsbyjs/gatsby/pull/25249 for more implementation details.
 */
const options = {
  body: {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text) => {
        // Satisfies AA audit.
        if (text === 'je ne sais quoi') return <em lang="fr">{text}</em>
        return <em>{text}</em>
      },
      [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
      [MARKS.CODE]: (text) => <code>{text}</code>,
    },
    renderNode: {
      [BLOCKS.DOCUMENT]: (_node, children) => children,
      [BLOCKS.PARAGRAPH]: (_node, children) => (
        <Text as="p" variant="bodyArticle" my="30px">
          {children}
        </Text>
      ),
      [BLOCKS.HR]: () => (
        <Box
          as="hr"
          sx={{ borderTop: '1px solid', borderTopColor: 'greyMedium' }}
        />
      ),
      [BLOCKS.HEADING_1]: (_node, children) => (
        <Heading as="h1" variant="title2">
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_2]: (_node, children) => (
        <Heading
          as="h2"
          variant="title3"
          mt={[5, 6, null, 7]}
          mb={[2, 3]}
          id={slugger.slug(children?.[0])}
          sx={headingAnchorOffsetStyles}
        >
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_3]: (_node, children) => (
        <Heading
          as="h3"
          variant="title4"
          mt={[4, 5, null, 6]}
          mb={2}
          id={slugger.slug(children?.[0])}
          sx={headingAnchorOffsetStyles}
        >
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_4]: (_node, children) => (
        <Heading
          as="h4"
          variant="title5"
          mt={[3, 4, null, 5]}
          mb={2}
          id={slugger.slug(children?.[0])}
          sx={headingAnchorOffsetStyles}
        >
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_5]: (_node, children) => (
        <Heading as="h5" variant="title6" mt={[2, 3, null, 4]} mb={2}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_6]: (_node, children) => <h6>{children}</h6>,
      [BLOCKS.EMBEDDED_ASSET]: (_node, _children) => (
        <GatsbyImage
          image={_node.data.target?.gatsbyImageData}
          alt={_node.data.target?.description}
        />
      ),
      [BLOCKS.EMBEDDED_ENTRY]: (_node, children) => {
        return (
          <Box my={[5, 6, null, 7]}>
            <MediaEmbed mediaEmbed={_node.data.target} />
          </Box>
        )
      },
      [BLOCKS.UL_LIST]: (_node, children) => (
        <Box as="ul" my="30px" sx={{ p: { m: 0 } }}>
          {children}
        </Box>
      ),
      [BLOCKS.OL_LIST]: (_node, children) => (
        <Box as="ol" my="30px" sx={{ p: { m: 0 } }}>
          {children}
        </Box>
      ),
      [BLOCKS.LIST_ITEM]: (_node, children) => <li>{children}</li>,
      [BLOCKS.QUOTE]: (_node, children) => (
        <Text
          as="blockquote"
          mx={0}
          my={(5, 6)}
          sx={{ p: { variant: 'text.quote1', m: 0 } }}
        >
          {children}
        </Text>
      ),
      [INLINES.ENTRY_HYPERLINK]: (node, _children) => (
        <Link to={slug(node)}>{node.content[0].value}</Link>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <a href={node.data.uri} {...hyperlinkProps(node.data.uri, children)}>
          {children}
        </a>
      ),
      [INLINES.ASSET_HYPERLINK]: (node, _children) =>
        defaultInline(INLINES.ASSET_HYPERLINK, node),
      [INLINES.EMBEDDED_ENTRY]: (node, _children) =>
        defaultInline(INLINES.EMBEDDED_ENTRY, node),
    },
  },
  ctaPrimary: {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => children,
      [INLINES.ENTRY_HYPERLINK]: (node, _children) => (
        <Link to={slug(node)} sx={{ variant: 'buttons.primary' }}>
          {node.content[0].value}
        </Link>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <a
          sx={{ variant: 'buttons.primary' }}
          href={node.data.uri}
          {...hyperlinkProps(node.data.uri, children)}
        >
          {children}
        </a>
      ),
    },
  },
  ctaText: {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => children,
      [INLINES.ENTRY_HYPERLINK]: (node, _children) => (
        <Link to={slug(node)} sx={{ variant: 'text.textLink' }}>
          {node.content[0].value}
        </Link>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <a
          sx={{ variant: 'text.textLink' }}
          href={node.data.uri}
          {...hyperlinkProps(node.data.uri, children)}
        >
          {children}
        </a>
      ),
    },
  },
  ctaTextBody3: {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => children,
      [INLINES.ENTRY_HYPERLINK]: (node, _children) => (
        <Link to={slug(node)} sx={{ variant: 'text.body3' }}>
          {node.content[0].value}
        </Link>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <a
          sx={{ variant: 'text.body3' }}
          href={node.data.uri}
          {...hyperlinkProps(node.data.uri, children)}
        >
          {children}
        </a>
      ),
    },
  },
  ctaLinktree: {
    renderNode: {
      [INLINES.HYPERLINK]: (node, children) => (
        <a
          sx={{ variant: 'buttons.linktree' }}
          href={node.data.uri}
          {...hyperlinkProps(node.data.uri, children)}
        >
          {children}
        </a>
      ),
    },
  },
}

const RichTextRenderer = ({ richText, optionSet = 'body' }) => {
  if (!richText) return <Fragment></Fragment>

  return <Fragment>{renderRichText(richText, options[optionSet])}</Fragment>
}

RichTextRenderer.propTypes = {
  richText: PropTypes.object,
}

export default RichTextRenderer
